import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterSearch from "../../components/Filtering/FilterSearch";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import ExportCsv from "../../components/buttons/ExportCSV";
import SearchButton from "../../components/buttons/SearchButton";
import DataTable from "../../components/DataTable/DataTable";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useTableControls } from "../../hooks/useTableControls";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import "./PrivateArea.css";
import Tabs from "../../components/DataTable/Tabs";
import { DetailsPopover } from "../../components/DataTable/DetailsPopover";
import { useNavigate } from "react-router-dom";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import { getDetailsFieldsByBlock } from "../../utils/constants";
import { getTextByLang } from "../../utils/formUtils";
import { extractIdFromIri } from "../../utils/extractFromIri";
import {
  buildParticipantsById,
  getParticipantLabelById,
} from "../../utils/participantLookup";
import StatusChip from "../../components/StatusChip/StatusChip";

import { useObservabilityStatistics } from "../../hooks/observability/useObservabilityStatistics";
import { useObservabilityTransfers } from "../../hooks/observability/useObservabilityTransfers";
import { useObservabilityContracts } from "../../hooks/observability/useObservabilityContracts";
import { useGetParticipantsOld } from "../../hooks/participants/useGetParticipantsOld";

function ActionLog() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("A");
  const [selectedBlock, setSelectedBlock] = useState("A");
  const { decoded, isProvider, token } = useAuthRoles();
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const navigate = useNavigate();
  const myOrgName = decoded?.organization;
  const handleRowClick = (row, blockKey) => {
    setSelectedRow(row);
    setSelectedBlock(blockKey);
    setDetailsOpen(true);
  };

  const handleNameClick = (record) => {
    let targetPath = "";

    switch (record.resource_type) {
      case "DATASET_OFFERING":
        targetPath = `/dataofferingdetail/${record.resource_id}`;
        break;
      case "VOCABULARY":
        targetPath = `/resourcedetail/${record.resource_id}`;
        break;
      default:
        if (record.contract_id || record.transfer_id) {
          targetPath = `/mycontracts`;
        } else {
          console.warn("Unknown resource type:", record.resource_type);
          return;
        }
    }

    navigate(targetPath, { state: { record } });
  };

  const detailsFieldsByBlock = getDetailsFieldsByBlock(t);

  const {
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    setPagination,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);

  const statsResourceType =
    activeTab === "A"
      ? "DATASET_OFFERING"
      : activeTab === "B"
        ? "VOCABULARY"
        : null;

  const statsQuery = useObservabilityStatistics({
    callApiWithWorker,
    resourceType: statsResourceType,
    pagination,
    enabled: Boolean(token && (activeTab === "A" || activeTab === "B")),
  });

  const transfersQuery = useObservabilityTransfers({
    callApiWithWorker,
    enabled: Boolean(token && activeTab === "C"),
  });

  const contractsQuery = useObservabilityContracts({
    callApiWithWorker,
    enabled: Boolean(token && activeTab === "D"),
  });
  const participantsQuery = useGetParticipantsOld({
    callApiWithWorker,
    lang: i18n.language,
    pageIndex: 1,
    pageSize: 1000,
    status: undefined,
  });

  const participantsById = useMemo(
    () => buildParticipantsById(participantsQuery.data?.content),
    [participantsQuery.data?.content],
  );

  const preferredUsername = decoded?.preferred_username;
  const participantsLoading =
    participantsQuery.isLoading || participantsQuery.isFetching;

  const rawData = useMemo(() => {
    let data = [];

    if (activeTab === "A" || activeTab === "B") data = statsQuery.data ?? [];
    if (activeTab === "C") data = transfersQuery.data ?? [];
    if (activeTab === "D") data = contractsQuery.data ?? [];

    if (!preferredUsername) return data;

    return data.filter((item) => item.username === preferredUsername);
  }, [
    activeTab,
    statsQuery.data,
    transfersQuery.data,
    contractsQuery.data,
    preferredUsername,
  ]);

  const tableData = useMemo(() => {
    if (activeTab === "A" || activeTab === "B") {
      return (rawData ?? []).map((item) => ({
        ...item,
        resource_name: getTextByLang(item.resource_name, i18n.language),
        publisher: participantsLoading
          ? "..."
          : getParticipantLabelById(
              item.publisher,
              participantsById,
              i18n.language,
            ),
      }));
    }

    return rawData ?? [];
  }, [
    rawData,
    activeTab,
    i18n.language,
    participantsById,
    participantsLoading,
  ]);

  const rowCount = tableData.length;

  const tableConfigs = useMemo(
    () => ({
      A: {
        label: t("activityDataset"),
        columns: [
          {
            header: t("privateArea.actionLog.resource_name"),
            accessorKey: "resource_name",
            meta: { width: "25%" },
          },
          {
            header: t("profilePage.contact.organization"),
            accessorKey: "publisher",
            meta: { width: "30%" },
          },
          {
            header: t("privateArea.actionLog.status"),
            accessorKey: "status",
            meta: { width: "20%" },
            cell: ({ getValue }) => (
              <StatusChip
                label={t(`privateArea.actionLog.states.${getValue()}`)}
                value={getValue()}
              />
            ),
          },
          {
            header: t("privateArea.actionLog.date"),
            accessorKey: "modified",
            meta: { width: "20%" },
            cell: ({ getValue }) => new Date(getValue()).toLocaleString(),
          },
        ],
      },
      B: {
        label: t("activityVocabulary"),
        columns: [
          {
            header: t("privateArea.actionLog.resource_name"),
            accessorKey: "resource_name",
            meta: { width: "25%" },
            // cell: ({ row }) => {
            //   const record = row.original;
            //   return (
            //     <span
            //       className="link-cell"
            //       onClick={() => handleNameClick(record)}
            //     >
            //       {record.resource_name}
            //     </span>
            //   );
            // },
          },
          {
            header: t("profilePage.contact.organization"),
            accessorKey: "publisher",
            meta: { width: "30%" },
          },
          {
            header: t("privateArea.actionLog.status"),
            accessorKey: "status",
            meta: { width: "20%" },
            cell: ({ getValue }) => (
              <StatusChip
                label={t(
                  `privateArea.actionLog.states.${extractIdFromIri(getValue())}`,
                )}
                value={extractIdFromIri(getValue())}
              />
            ),
          },
          {
            header: t("privateArea.actionLog.date"),
            accessorKey: "modified",
            meta: { width: "20%" },
            cell: ({ getValue }) => new Date(getValue()).toLocaleString(),
          },
        ],
      },
      C: {
        label: t("mytransfers"),
        columns: [
          {
            header: t("privateArea.actionLog.offer"),
            accessorKey: "resource_title",
            meta: { width: "15%" },
            cell: ({ row }) => {
              const record = row.original;
              return (
                <span
                  className="link-cell"
                  onClick={() => handleNameClick(record)}
                >
                  {record.resource_title}
                </span>
              );
            },
          },
          {
            header: t("roles.consumer"),
            accessorKey: "consumer_name",
            meta: { width: "15%" },
          },
          {
            header: t("roles.provider"),
            accessorKey: "provider_name",
            meta: { width: "15%" },
          },
          {
            header: t("privateArea.actionLog.transfer"),
            accessorKey: "transfer_type",
            meta: { width: "15%" },
          },
          {
            header: t("privateArea.actionLog.status"),
            accessorKey: "status",
            meta: { width: "15%" },
            cell: ({ getValue }) => (
              <StatusChip
                label={t(`privateArea.actionLog.states.${getValue()}`)}
                value={getValue()}
              />
            ),
          },
          {
            header: t("privateArea.actionLog.created"),
            accessorKey: "created",
            meta: { width: "10%" },
            cell: ({ getValue }) => new Date(getValue()).toLocaleString(),
          },
          {
            header: t("privateArea.actionLog.disp"),
            accessorKey: "registration_date",
            meta: { width: "15%" },
            cell: ({ getValue }) => new Date(getValue() + "Z").toLocaleString(),
          },
        ],
      },
      D: {
        label: t("contracts"),
        columns: [
          {
            header: t("privateArea.actionLog.offer"),
            accessorKey: "resource_title",
            meta: { width: "20%" },
            cell: ({ row }) => {
              const record = row.original;
              return (
                <span
                  className="link-cell"
                  onClick={() => handleNameClick(record)}
                >
                  {record.resource_title}
                </span>
              );
            },
          },
          {
            header: t("roles.consumer"),
            accessorKey: "consumer_name",
            meta: { width: "15%" },
          },
          {
            header: t("roles.provider"),
            accessorKey: "provider_name",
            meta: { width: "15%" },
          },
          {
            header: t("privateArea.actionLog.status"),
            accessorKey: "status",
            meta: { width: "10%" },
            cell: ({ getValue }) => (
              <StatusChip
                label={t(`privateArea.actionLog.states.${getValue()}`)}
                value={getValue()}
              />
            ),
          },
          {
            header: t("privateArea.actionLog.startDate"),
            accessorKey: "start_date",
            meta: { width: "15%" },
            cell: ({ getValue }) => new Date(getValue()).toLocaleString(),
          },
        ],
      },
    }),
    [t, myOrgName],
  );

  const currentConfig = tableConfigs[activeTab];

  const filteredData = useFilteredData(
    tableData,
    searchTerm,
    filters,
    currentConfig.columns,
  );

  const isLoading =
    activeTab === "A" || activeTab === "B"
      ? statsQuery.isLoading ||
        statsQuery.isFetching ||
        participantsQuery.isLoading ||
        participantsQuery.isFetching
      : activeTab === "C"
        ? transfersQuery.isLoading || transfersQuery.isFetching
        : activeTab === "D"
          ? contractsQuery.isLoading || contractsQuery.isFetching
          : false;

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>

      <div className="container containerflex">
        <div className="w90 labeltittle">
          <h2 className="tittlecatalog">{t("privateArea.actionLog.title")}</h2>
          {isProvider && (
            <ExportCsv
              tableData={filteredData}
              resourceType={
                activeTab === "A"
                  ? "Dataset_offerings"
                  : activeTab === "B"
                    ? "Vocabularies"
                    : activeTab === "C"
                      ? "Transfers"
                      : activeTab === "D"
                        ? "Contracts"
                        : ""
              }
            />
          )}
        </div>

        <div className={"w86 filteractions pt3"}>
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>

        <div>
          <FilterRightBox
            open={open}
            onClose={handleClose}
            onApplyFilters={handleApplyFilters}
          />
        </div>

        <Tabs
          tabs={tableConfigs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <div className="w90 bgwhite mb2">
          <div className="plr3 ptb">
            <DataTable
              columns={currentConfig.columns}
              data={filteredData}
              onDetails={(row) => handleRowClick(row, activeTab)}
              showActions={true}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      <DetailsPopover
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={t("privateArea.actionLog.rowDetails")}
        data={selectedRow}
        fields={
          selectedBlock
            ? detailsFieldsByBlock[selectedBlock]?.(selectedRow) || []
            : []
        }
      />
    </section>
  );
}

export default ActionLog;
